import { FieldState, FormState } from 'formstate';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';

import { InputFieldState, formStateToJS, setFormStateValues } from '@servicetitan/form';
import { TableState, InMemoryDataSource, PossibleFormState } from '@servicetitan/table';
import { NotificationsService, Status } from '@servicetitan/notifications';
import { inject, injectable } from '@servicetitan/react-ioc';

import { FeaturesStore } from '@servicetitan/application-data/features';
import {
    AvailableNumberModel,
    DialpadNumberAssignmentTarget,
    DialpadUserDTO,
    IsDialpadAdminStore,
    PhoneNumberModel,
    PhoneNumberSearchRequestModel,
    PhonesPro,
} from '@servicetitan/application-data/telecom';
import { BrandApi, BrandModel } from '@servicetitan/application-data/dist/squads/telecom/brand.api';
import { getPhoneNumberFormState } from '../utils/phone-number-form-state-helper';
import { ChangeEvent } from 'react';
import { InputOnChangeData } from '@servicetitan/design-system';

export enum AddNumberModalStep {
    Search = 1,
    Select = 2,
    Edit = 3,
    Confirm = 4,
}

@injectable()
export class PhoneNumbersStore {
    @observable users: DialpadUserDTO[] = [];
    @observable targets: DialpadNumberAssignmentTarget[] = [];
    @observable editNumberId?: string;
    @observable addEditNumberError?: string;
    @observable dialpadOfficeSettingsUrl?: string;
    @observable saveInProgress = false;
    @observable searchInProgress = false;
    @observable numberAssignmentLoadingInProgress = false;
    @observable numberAssignmentSearchTerm = '';
    @observable modalStep?: AddNumberModalStep;
    @observable availableNumbers: AvailableNumberModel[] = [];
    @observable availableNumbersGridsState = new TableState<AvailableNumberModel, string>();
    @observable phoneNumbersGridState = new TableState<PhoneNumberModel, string>({
        getFormState: row => setFormStateValues(getPhoneNumberFormState(), row),
        pageSize: 10,
    });
    @observable savedBrandModel?: BrandModel;
    @observable shouldDetachTcrCampaignFromPhone = false;

    formState = new FormState({
        searchFormState: new FormState({
            isLocal: new FieldState<boolean>(true),
            areaCode: new InputFieldState<string>(''),
            zipCode: new InputFieldState<string>(''),
            contains: new InputFieldState<string>(''),
        }),
        editFormState: getPhoneNumberFormState(),
    });

    constructor(
        @inject(PhonesPro) private phonesProApi: PhonesPro,
        @inject(FeaturesStore) private featureStore: FeaturesStore,
        @inject(IsDialpadAdminStore) private isDialpadAdminStore: IsDialpadAdminStore,
        @inject(NotificationsService) private notifications: NotificationsService,
        @inject(BrandApi) private brandApi: BrandApi
    ) {
        makeObservable(this);
    }

    @computed get isDialpadEnabled() {
        return (
            this.isDialpadAdminStore.isDialpadAdmin ||
            this.featureStore.features.EnableServiceTitanPhonesUser
        );
    }

    @action openAddModal = () => {
        this.modalStep = AddNumberModalStep.Search;
    };

    @action openEditModal = (phone: PhoneNumberModel) => {
        this.editNumberId = this.numberIdSelector(phone);
        this.modalStep = AddNumberModalStep.Edit;

        if (this.isDialpadEnabled) {
            this.loadNumberAssignment();
        }
        this.shouldDetachTcrCampaignFromPhone = false;
    };

    @action closeModal = () => {
        this.modalStep = undefined;
        this.availableNumbers = [];
        this.availableNumbersGridsState.reset();
        this.editNumberId = undefined;
        this.addEditNumberError = undefined;
        this.dialpadOfficeSettingsUrl = undefined;
        this.formState.reset();
        this.phoneNumbersGridState.cancelEditAll();
        this.shouldDetachTcrCampaignFromPhone = false;
    };

    @action goForward = () => (this.modalStep! += 1);
    @action goBack = () => {
        this.addEditNumberError = undefined;
        this.dialpadOfficeSettingsUrl = undefined;
        this.formState.$.editFormState.reset();
        this.modalStep! -= 1;
    };

    @action handleSearch = (event: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        this.numberAssignmentSearchTerm = data.value;
    };

    @computed get canAddMoreNumbers() {
        return this.availableNumbers.length > this.availableNumbersGridsState.totalCount;
    }

    @action searchNumbers = async () => {
        if (this.searchInProgress) {
            return;
        }
        this.searchInProgress = true;
        this.goForward();
        try {
            const result = await this.getAvailablePhoneNumbers(
                formStateToJS(this.formState.$.searchFormState)
            );
            runInAction(() => (this.availableNumbers = result || []));
            this.availableNumbersGridsState.setDataSource(
                new InMemoryDataSource(this.availableNumbers.slice(0, 5), this.numberIdSelector)
            );
        } finally {
            runInAction(() => (this.searchInProgress = false));
        }
    };

    @action loadMoreNumbers = async () => {
        if (this.canAddMoreNumbers) {
            await Promise.all(
                this.availableNumbers
                    .slice(
                        this.availableNumbersGridsState.data.length,
                        this.availableNumbersGridsState.data.length + 5
                    )
                    .map(x => this.availableNumbersGridsState.addToDataSource(x))
            );
        }
    };

    @action saveNumber = async (form: PossibleFormState<PhoneNumberModel>, isNew: boolean) => {
        if (this.saveInProgress) {
            return;
        }
        const result = await form.validate();
        if (result.hasError) {
            return;
        }

        const model = formStateToJS(form);

        try {
            runInAction(() => (this.saveInProgress = true));
            const response = await this.phonesProApi.savePhoneNumber(model);
            if (response?.data) {
                const executionResult = response.data!;
                if (!executionResult.success) {
                    runInAction(() => (this.addEditNumberError = executionResult.message));
                } else {
                    if (this.shouldDetachTcrCampaignFromPhone) {
                        const detachResponse = await this.phonesProApi.detachTcrCampaignFromPhone(
                            model.phoneNumber!,
                            model.tcrCampaignId!
                        );
                        const detachExecutionResult = detachResponse.data;
                        if (!detachExecutionResult.success) {
                            runInAction(
                                () =>
                                    (this.addEditNumberError =
                                        'Campaign dissasociation failed. ' +
                                        detachExecutionResult.message)
                            );
                            return model;
                        }
                        form.$.tcrCampaignId.reset(undefined);
                        form.$.tcrCampaignExternalId.reset(undefined);
                        this.phoneNumbersGridState.saveEdit(model);
                    }
                    const result = executionResult.result!;
                    if (isNew) {
                        this.phoneNumbersGridState.addToDataSource(result.numberModel!, false, 0);
                    } else {
                        form.$.forwardTo.reset(result.numberModel!.forwardTo);
                        this.phoneNumbersGridState.saveEdit(model);
                    }
                    if (
                        this.featureStore.features.EnableServiceTitanPhonesUser &&
                        isNew &&
                        !model.isDirectDial &&
                        !model.assignmentTarget
                    ) {
                        runInAction(
                            () => (this.dialpadOfficeSettingsUrl = result.dialpadOfficeSettingsUrl)
                        );
                        this.goForward();
                    } else {
                        this.closeModal();
                    }
                    return model;
                }
            }
        } catch {
            this.notifications.show({
                status: Status.Error,
                title: 'Save Phone Number',
                message: 'An error occurred, please try again.',
            });
        } finally {
            runInAction(() => (this.saveInProgress = false));
        }
    };

    numberIdSelector = (x: PhoneNumberModel | AvailableNumberModel) => x.phoneNumber!;

    loadData = async () => {
        this.loadPhoneNumbers();
        this.loadUsers();

        if (this.isDialpadEnabled) {
            this.loadDialpadTargets();
        }
        this.loadBrandData();
    };

    loadPhoneNumbers = async () => {
        const phoneNumbers = await this.phonesProApi.getPhoneNumbers();
        if (phoneNumbers?.data) {
            this.phoneNumbersGridState.setDataSource(
                new InMemoryDataSource<PhoneNumberModel, string>(
                    phoneNumbers.data,
                    this.numberIdSelector
                )
            );
        }
    };

    loadUsers = async () => {
        const users = await this.phonesProApi.getUsers();
        if (users?.data) {
            runInAction(() => (this.users = users!.data!.filter(x => x.licenseType)));
        }
    };

    loadNumberAssignment = async () => {
        const numberFormState = this.editNumberId
            ? this.phoneNumbersGridState.inEdit.get(this.editNumberId!)!.form
            : this.formState.$.editFormState;

        this.numberAssignmentLoadingInProgress = true;

        const assignment = await this.phonesProApi.getDialpadNumberAssignment(
            numberFormState.$.phoneNumber.value!
        );
        if (assignment?.data) {
            numberFormState.$.assignmentTarget.reset(assignment!.data);
        }

        runInAction(() => (this.numberAssignmentLoadingInProgress = false));
    };

    loadDialpadTargets = async () => {
        const targets = await this.phonesProApi.getDialpadNumberAssignmentTargets();
        if (targets?.data) {
            runInAction(() => (this.targets = targets!.data!));
        }
    };

    loadBrandData = async () => {
        const brandData = (await this.brandApi.getData()).data.brandModel;
        runInAction(() => (this.savedBrandModel = brandData));
    };

    protected async getAvailablePhoneNumbers(
        filter: PhoneNumberSearchRequestModel
    ): Promise<AvailableNumberModel[]> {
        return this.phonesProApi.getAvailablePhoneNumbers(filter).then(({ data }) => data);
    }

    isRegistrationForTextingNotStarted(): boolean {
        return !this.savedBrandModel?.status;
    }
}
